import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import SiteContainer from './SiteContainer';
import Eyebrow from './Eyebrow';
import Heading from './Heading';

/**
 * The top of a redesigned page. Renders **the page's only `<h1>`**, in a `<section>`
 * named by it. Sits on the paper canvas; the fixed site header's offset is the
 * layout's job, not this component's. The section is a `.site-light` surface (rust
 * focus ring); on a navy band pass `className="site-dark …"` (site.css lets
 * `.site-dark` win on the same element).
 *
 * Props:
 * - `title` (required): the h1 content; may contain `<em>` (italic accent) and `<br />`
 * - `titleSize`: 'page' (default, interior H1) | 'display' (home hero)
 * - `accent`: colour of `<em>` in the title — 'inherit' (default) | 'rust'
 * - `titleId`: id of the h1 (default from useId, so two heroes never collide)
 * - `eyebrow`: string (wrapped in <Eyebrow>) or your own node (e.g. <Eyebrow tone="muted">)
 * - `eyebrowIndex`: number/string for "01 / …" when `eyebrow` is a string
 * - `lede`: the intro paragraph (muted, ~590px measure)
 * - `actions`: node — usually one or two <Button>s; stacks full width below 640px
 * - `fine`: small print under the actions ("An enquiry does not reserve a slot.")
 * - `media`: node for the right column (split 1.12fr / 0.88fr from 800px; stacks below)
 * - `align`: 'start' (default) | 'center' (centred intro, 1050px measure; ignored with media)
 * - `className`, `children` (extra content under the fine print); other props go on the section.
 */
export default function PageHero({
  title,
  titleSize = 'page',
  accent = 'inherit',
  titleId,
  eyebrow,
  eyebrowIndex,
  lede,
  actions,
  fine,
  media,
  align = 'start',
  className,
  children,
  ...rest
}) {
  const autoId = useId();
  const headingId = titleId || `site-hero-${autoId}`;
  const centered = align === 'center' && !media;
  const hasEyebrow = eyebrow !== undefined && eyebrow !== null && eyebrow !== false && eyebrow !== '';
  const eyebrowNode = hasEyebrow && !React.isValidElement(eyebrow)
    ? <Eyebrow index={eyebrowIndex}>{eyebrow}</Eyebrow>
    : eyebrow;

  return (
    <section aria-labelledby={headingId} className={cn('site-light text-navy', className)} {...rest}>
      <SiteContainer
        className={cn(
          'pb-11 pt-6 nav:pb-16',
          media && 'grid items-center gap-[2.375rem] nav:grid-cols-[1.12fr_0.88fr] nav:gap-[2.1875rem] lg:gap-[4.25rem]',
          centered && 'text-center',
        )}
      >
        <div className={cn('min-w-0', centered && 'mx-auto max-w-[65.625rem]')}>
          {hasEyebrow ? eyebrowNode : null}
          <Heading
            level={1}
            size={titleSize}
            accent={accent}
            id={headingId}
            className={cn('mb-[1.625rem]', hasEyebrow ? 'mt-5' : 'mt-0')}
          >
            {title}
          </Heading>
          {lede ? (
            <p
              className={cn(
                'max-w-[36.875rem] text-[1.0625rem] leading-[1.65] text-brand-muted nav:text-[1.1875rem]',
                centered && 'mx-auto',
              )}
            >
              {lede}
            </p>
          ) : null}
          {actions ? (
            <div
              className={cn(
                'mt-[1.875rem] flex flex-col items-stretch gap-3.5 sm:flex-row sm:flex-wrap sm:items-center',
                centered && 'sm:justify-center',
              )}
            >
              {actions}
            </div>
          ) : null}
          {fine ? (
            <p className={cn('mt-[1.125rem] max-w-[66ch] text-[0.8125rem] leading-[1.65] text-brand-muted', centered && 'mx-auto')}>
              {fine}
            </p>
          ) : null}
          {children}
        </div>
        {media ? <div className="min-w-0">{media}</div> : null}
      </SiteContainer>
    </section>
  );
}
